import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, MessageCircle, Sparkles, CheckCheck } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(null); // holds the key currently saving
  const [phone, setPhone] = useState("");
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [waLeaving, setWaLeaving] = useState(false);
  const remindersOn = user?.email_reminders_enabled !== false;
  const dailyOn = user?.email_daily_updates_enabled !== false;
  const whatsappOn = !!user?.wants_whatsapp;

  const savePref = async (key, value, labels) => {
    setSaving(key);
    try {
      await api.put("/auth/me/preferences", { [key]: value });
      await refreshUser();
      toast.success(value ? labels.on : labels.off);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const toggleReminders = (v) => savePref("email_reminders_enabled", v, {
    on: "Match reminders switched on — we'll email you ~2h before kickoff.",
    off: "Match reminders switched off. You won't receive kickoff emails.",
  });

  const toggleDaily = (v) => savePref("email_daily_updates_enabled", v, {
    on: "Daily league updates switched on — we'll email you after each matchday.",
    off: "Daily league updates switched off.",
  });

  const joinWhatsapp = async () => {
    setWaSubmitting(true);
    try {
      await api.post("/auth/whatsapp-opt-in", {
        phone_e164: phone.trim() || null,
      });
      toast.success("You're in — check your email for the WhatsApp group link");
      setPhone("");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWaSubmitting(false);
    }
  };

  const leaveWhatsapp = async () => {
    setWaLeaving(true);
    try {
      await api.post("/auth/whatsapp-opt-out");
      toast.success("You've left the WhatsApp banter group.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWaLeaving(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="settings-page">
        <header className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Your account
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-1">
            Settings.
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Fine-tune what shows up in your inbox. You can change these at any time.
          </p>
        </header>

        <section
          className="border border-zinc-200 rounded-sm bg-white p-6"
          data-testid="settings-notifications-card"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Bell className="h-3 w-3" /> Notifications
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            Email notifications.
          </h2>

          <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-lg">
              <p className="font-heading text-base font-bold text-zinc-950">Match reminders</p>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                We&apos;ll email you approximately <strong>2 hours before kickoff</strong> for every upcoming
                match — but only if you <strong>haven&apos;t already submitted your prediction</strong>. Delivered to <strong>{user?.email}</strong>.
              </p>
            </div>
            <Switch
              checked={remindersOn}
              onCheckedChange={toggleReminders}
              disabled={saving === "email_reminders_enabled"}
              data-testid="settings-reminders-toggle"
              aria-label="Toggle match reminder emails"
            />
          </div>

          <p className="mt-3 text-xs text-zinc-500" data-testid="settings-reminders-state">
            {saving === "email_reminders_enabled" ? "Saving…" : remindersOn ? "You're subscribed to match reminders." : "You're unsubscribed."}
          </p>

          <div className="mt-8 pt-6 border-t border-zinc-100 flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-lg">
              <p className="font-heading text-base font-bold text-zinc-950">Daily league updates</p>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                After the last match of each matchday is scored, we&apos;ll email participants a recap
                with your <strong>daily rank &amp; points</strong> and the <strong>top-10 overall standings</strong>.
                Match scores are never included.
              </p>
            </div>
            <Switch
              checked={dailyOn}
              onCheckedChange={toggleDaily}
              disabled={saving === "email_daily_updates_enabled"}
              data-testid="settings-daily-toggle"
              aria-label="Toggle daily league update emails"
            />
          </div>

          <p className="mt-3 text-xs text-zinc-500" data-testid="settings-daily-state">
            {saving === "email_daily_updates_enabled" ? "Saving…" : dailyOn ? "You're subscribed to daily league updates." : "You're unsubscribed."}
          </p>
        </section>

        <section
          className="mt-6 border border-zinc-200 rounded-sm bg-white p-6"
          data-testid="settings-whatsapp-card"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 inline-flex items-center gap-1.5">
            <MessageCircle className="h-3 w-3" /> Community
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            WhatsApp banter group.
          </h2>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed max-w-lg">
            Live banter, instant lineups, and the inevitable disputes about throw-ins. Opt in and
            we&apos;ll email you the group join-link.
          </p>

          {whatsappOn ? (
            <div className="mt-6 flex items-start justify-between gap-6 flex-wrap" data-testid="settings-whatsapp-joined">
              <div className="max-w-lg">
                <p className="inline-flex items-center gap-2 font-heading text-base font-bold text-emerald-700">
                  <CheckCheck className="h-4 w-4" /> You&apos;re in the group.
                </p>
                <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                  The invite link was sent to <strong>{user?.email}</strong>
                  {user?.phone_e164 ? <> · Phone on file: <strong>{user.phone_e164}</strong></> : null}.
                  Changed your mind? You can leave any time.
                </p>
              </div>
              <Button
                type="button"
                onClick={leaveWhatsapp}
                disabled={waLeaving}
                data-testid="settings-whatsapp-leave-btn"
                variant="outline"
                className="h-10 px-4 rounded-sm border-zinc-300 text-zinc-950 hover:bg-zinc-50"
              >
                {waLeaving ? "Leaving…" : "Leave group"}
              </Button>
            </div>
          ) : (
            <div className="mt-6" data-testid="settings-whatsapp-optin">
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Phone number (optional)
              </Label>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44 7123 456789"
                  data-testid="settings-whatsapp-phone-input"
                  className="h-10 w-64 max-w-full rounded-sm border-zinc-300"
                />
                <Button
                  type="button"
                  onClick={joinWhatsapp}
                  disabled={waSubmitting}
                  data-testid="settings-whatsapp-join-btn"
                  className="h-10 px-4 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2"
                >
                  <CheckCheck className="h-4 w-4" />
                  {waSubmitting ? "Sending…" : "Join the group"}
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500">
                Phone helps Carl add you manually if your email link gets lost in spam. Optional.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
